import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/becomehost.css';

const BecomeHost = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    // Property Information
    propertyType: '',
    propertyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    bedrooms: '',
    bathrooms: '',
    maxGuests: '',
    amenities: [],
    description: '',
    // Experience
    experience: '',
    // Bank Details
    bankAccount: '',
    ifscCode: '',
    accountHolder: '',
    // Terms
    agreeTerms: false,
    agreePrivacy: false
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const propertyTypes = ['House', 'Apartment', 'Villa', 'Cottage', 'Resort', 'Homestay', 'Tent', 'Other'];
  
  const amenitiesList = [
    'WiFi', 'Parking', 'Kitchen', 'AC', 'Heating', 'TV', 
    'Balcony', 'Garden', 'Pool', 'Gym', 'Laundry', 'Pet Friendly'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAmenityChange = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.phone.match(/^[0-9]{10}$/)) newErrors.phone = 'Enter valid 10-digit phone number';
      if (!formData.location.trim()) newErrors.location = 'Location is required';
    }
    
    if (currentStep === 2) {
      if (!formData.propertyType) newErrors.propertyType = 'Property type is required';
      if (!formData.propertyName.trim()) newErrors.propertyName = 'Property name is required';
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip code is required';
      if (!formData.zipCode.match(/^[0-9]{6}$/)) newErrors.zipCode = 'Enter valid 6-digit PIN code';
      if (!formData.bedrooms) newErrors.bedrooms = 'Number of bedrooms is required';
      if (!formData.bathrooms) newErrors.bathrooms = 'Number of bathrooms is required';
      if (!formData.maxGuests) newErrors.maxGuests = 'Max guests is required';
      if (!formData.description.trim()) newErrors.description = 'Property description is required';
    }
    
    if (currentStep === 3) {
      if (!formData.experience) newErrors.experience = 'Experience level is required';
    }
    
    if (currentStep === 4) {
      if (!formData.bankAccount.trim()) newErrors.bankAccount = 'Bank account number is required';
      if (!formData.ifscCode.trim()) newErrors.ifscCode = 'IFSC code is required';
      if (!formData.accountHolder.trim()) newErrors.accountHolder = 'Account holder name is required';
      if (!formData.agreeTerms) newErrors.agreeTerms = 'You must agree to terms and conditions';
      if (!formData.agreePrivacy) newErrors.agreePrivacy = 'You must agree to privacy policy';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      console.log('Submitting form:', formData);
      setSubmitted(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="host-success-container">
        <div className="success-card">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h1>Application Submitted!</h1>
          <p>Thank you for choosing to become a RedGoTrip host.</p>
          <p className="success-subtitle">Our team will review your application and contact you within 24-48 hours.</p>
          <button onClick={() => navigate('/')} className="success-btn">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Become a Host | RedGoTrip"
        description="Join RedGoTrip as a property owner. List your accommodation and start earning with our platform."
        keywords="become host, property owner, list accommodation, RedGoTrip host"
      />

      <div className="become-host-page">
        {/* Hero Section */}
        <section className="host-hero">
          <div className="host-hero-bg"></div>
          <div className="host-hero-content">
            <div className="hero-badge">
              <i className="fas fa-home"></i>
              <span>Join Our Community</span>
            </div>
            <h1>Become a <span className="gradient-text">RedGoTrip</span> Host</h1>
            <p>Share your property and earn extra income with India's fastest growing travel platform</p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Active Hosts</span>
              </div>
              <div className="stat">
                <span className="stat-number">10k+</span>
                <span className="stat-label">Happy Guests</span>
              </div>
              <div className="stat">
                <span className="stat-number">₹50L+</span>
                <span className="stat-label">Earned by Hosts</span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="host-benefits">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">Why Host With Us</span>
              <h2>Start Your Hosting Journey Today</h2>
              <p>Join thousands of hosts who are earning extra income through RedGoTrip</p>
            </div>
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-rupee-sign"></i>
                </div>
                <h3>Maximize Earnings</h3>
                <p>Set competitive prices and earn up to 85% of booking value with dynamic pricing tools</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <h3>Full Protection</h3>
                <p>₹1 crore host protection insurance covering property damage and liability</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-headset"></i>
                </div>
                <h3>Dedicated Support</h3>
                <p>24/7 priority support with dedicated host success manager for guidance</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Smart Dashboard</h3>
                <p>Real-time analytics, calendar management, and automated payment system</p>
              </div>
            </div>
          </div>
        </section>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className="container">
            <div className="steps-wrapper">
              {[
                { step: 1, title: 'Personal', icon: 'fas fa-user' },
                { step: 2, title: 'Property', icon: 'fas fa-home' },
                { step: 3, title: 'Experience', icon: 'fas fa-star' },
                { step: 4, title: 'Bank & Terms', icon: 'fas fa-bank' }
              ].map((item) => (
                <div 
                  key={item.step} 
                  className={`step ${currentStep >= item.step ? 'active' : ''} ${currentStep > item.step ? 'completed' : ''}`}
                >
                  <div className="step-circle">
                    {currentStep > item.step ? <i className="fas fa-check"></i> : <i className={item.icon}></i>}
                  </div>
                  <div className="step-label">{item.title}</div>
                  <div className="step-line"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form Section */}
        <section className="host-form-section">
          <div className="container">
            <div className="form-card">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                  <div className="form-step animate-step">
                    <div className="step-header">
                      <h3>Personal Information</h3>
                      <p>Tell us about yourself to get started</p>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter your first name"
                          className={errors.firstName ? 'error' : ''}
                        />
                        {errors.firstName && <span className="error-msg">{errors.firstName}</span>}
                      </div>
                      <div className="form-group">
                        <label>Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter your last name"
                          className={errors.lastName ? 'error' : ''}
                        />
                        {errors.lastName && <span className="error-msg">{errors.lastName}</span>}
                      </div>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          className={errors.email ? 'error' : ''}
                        />
                        {errors.email && <span className="error-msg">{errors.email}</span>}
                      </div>
                      <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="10-digit mobile number"
                          className={errors.phone ? 'error' : ''}
                        />
                        {errors.phone && <span className="error-msg">{errors.phone}</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Current Location *</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="City, State"
                        className={errors.location ? 'error' : ''}
                      />
                      {errors.location && <span className="error-msg">{errors.location}</span>}
                    </div>
                  </div>
                )}

                {/* Step 2: Property Information */}
                {currentStep === 2 && (
                  <div className="form-step animate-step">
                    <div className="step-header">
                      <h3>Property Information</h3>
                      <p>Share details about your property</p>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Property Type *</label>
                        <select
                          name="propertyType"
                          value={formData.propertyType}
                          onChange={handleInputChange}
                          className={errors.propertyType ? 'error' : ''}
                        >
                          <option value="">Select property type</option>
                          {propertyTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {errors.propertyType && <span className="error-msg">{errors.propertyType}</span>}
                      </div>
                      <div className="form-group">
                        <label>Property Name *</label>
                        <input
                          type="text"
                          name="propertyName"
                          value={formData.propertyName}
                          onChange={handleInputChange}
                          placeholder="Give your property a name"
                          className={errors.propertyName ? 'error' : ''}
                        />
                        {errors.propertyName && <span className="error-msg">{errors.propertyName}</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Full Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Street address"
                        className={errors.address ? 'error' : ''}
                      />
                      {errors.address && <span className="error-msg">{errors.address}</span>}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="City"
                          className={errors.city ? 'error' : ''}
                        />
                        {errors.city && <span className="error-msg">{errors.city}</span>}
                      </div>
                      <div className="form-group">
                        <label>State *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="State"
                          className={errors.state ? 'error' : ''}
                        />
                        {errors.state && <span className="error-msg">{errors.state}</span>}
                      </div>
                      <div className="form-group">
                        <label>PIN Code *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          placeholder="6-digit PIN code"
                          className={errors.zipCode ? 'error' : ''}
                          maxLength="6"
                        />
                        {errors.zipCode && <span className="error-msg">{errors.zipCode}</span>}
                      </div>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Bedrooms *</label>
                        <input
                          type="number"
                          name="bedrooms"
                          value={formData.bedrooms}
                          onChange={handleInputChange}
                          placeholder="Number of bedrooms"
                          className={errors.bedrooms ? 'error' : ''}
                          min="1"
                        />
                        {errors.bedrooms && <span className="error-msg">{errors.bedrooms}</span>}
                      </div>
                      <div className="form-group">
                        <label>Bathrooms *</label>
                        <input
                          type="number"
                          name="bathrooms"
                          value={formData.bathrooms}
                          onChange={handleInputChange}
                          placeholder="Number of bathrooms"
                          className={errors.bathrooms ? 'error' : ''}
                          min="1"
                          step="0.5"
                        />
                        {errors.bathrooms && <span className="error-msg">{errors.bathrooms}</span>}
                      </div>
                      <div className="form-group">
                        <label>Max Guests *</label>
                        <input
                          type="number"
                          name="maxGuests"
                          value={formData.maxGuests}
                          onChange={handleInputChange}
                          placeholder="Maximum guests allowed"
                          className={errors.maxGuests ? 'error' : ''}
                          min="1"
                        />
                        {errors.maxGuests && <span className="error-msg">{errors.maxGuests}</span>}
                      </div>
                    </div>
                    
             

                    <div className="form-group">
                      <label>Property Description *</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe your property, nearby attractions, what makes it special..."
                        rows="5"
                        className={errors.description ? 'error' : ''}
                      ></textarea>
                      {errors.description && <span className="error-msg">{errors.description}</span>}
                    </div>
                  </div>
                )}

                {/* Step 3: Experience */}
                {currentStep === 3 && (
                  <div className="form-step animate-step">
                    <div className="step-header">
                      <h3>Hosting Experience</h3>
                      <p>Tell us about your experience in hospitality</p>
                    </div>
                    <div className="form-group">
                      <label>How would you rate your hosting experience? *</label>
                      <div className="experience-options">
                        <label className={`exp-option ${formData.experience === 'first-time' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="experience"
                            value="first-time"
                            checked={formData.experience === 'first-time'}
                            onChange={handleInputChange}
                          />
                          <span>🌟 First Time Host</span>
                        </label>
                        <label className={`exp-option ${formData.experience === '1-2-years' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="experience"
                            value="1-2-years"
                            checked={formData.experience === '1-2-years'}
                            onChange={handleInputChange}
                          />
                          <span>📈 1-2 Years Experience</span>
                        </label>
                        <label className={`exp-option ${formData.experience === '3-5-years' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="experience"
                            value="3-5-years"
                            checked={formData.experience === '3-5-years'}
                            onChange={handleInputChange}
                          />
                          <span>🏆 3-5 Years Experience</span>
                        </label>
                        <label className={`exp-option ${formData.experience === '5-plus-years' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="experience"
                            value="5-plus-years"
                            checked={formData.experience === '5-plus-years'}
                            onChange={handleInputChange}
                          />
                          <span>⭐ 5+ Years Professional</span>
                        </label>
                      </div>
                      {errors.experience && <span className="error-msg">{errors.experience}</span>}
                    </div>
                    <div className="info-box">
                      <i className="fas fa-info-circle"></i>
                      <p>Don't worry if you're a first-time host! Our dedicated support team will guide you through the entire process.</p>
                    </div>
                  </div>
                )}

                {/* Step 4: Bank Details & Terms */}
                {currentStep === 4 && (
                  <div className="form-step animate-step">
                    <div className="step-header">
                      <h3>Bank Details & Agreement</h3>
                      <p>Set up your payment information</p>
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Account Holder Name *</label>
                        <input
                          type="text"
                          name="accountHolder"
                          value={formData.accountHolder}
                          onChange={handleInputChange}
                          placeholder="Name as per bank account"
                          className={errors.accountHolder ? 'error' : ''}
                        />
                        {errors.accountHolder && <span className="error-msg">{errors.accountHolder}</span>}
                      </div>
                      <div className="form-group">
                        <label>Bank Account Number *</label>
                        <input
                          type="text"
                          name="bankAccount"
                          value={formData.bankAccount}
                          onChange={handleInputChange}
                          placeholder="Account number"
                          className={errors.bankAccount ? 'error' : ''}
                        />
                        {errors.bankAccount && <span className="error-msg">{errors.bankAccount}</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>IFSC Code *</label>
                      <input
                        type="text"
                        name="ifscCode"
                        value={formData.ifscCode}
                        onChange={handleInputChange}
                        placeholder="e.g., SBIN0001234"
                        className={errors.ifscCode ? 'error' : ''}
                      />
                      {errors.ifscCode && <span className="error-msg">{errors.ifscCode}</span>}
                    </div>
                    <div className="terms-section">
                      <div className="checkbox-wrapper">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="agreeTerms"
                            checked={formData.agreeTerms}
                            onChange={handleInputChange}
                          />
                          <span>I agree to the <a href="/terms">Terms and Conditions</a> and <a href="/privacy">Privacy Policy</a> *</span>
                        </label>
                        {errors.agreeTerms && <span className="error-msg">{errors.agreeTerms}</span>}
                      </div>
                      <div className="checkbox-wrapper">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="agreePrivacy"
                            checked={formData.agreePrivacy}
                            onChange={handleInputChange}
                          />
                          <span>I consent to receive communications via email and WhatsApp regarding my application *</span>
                        </label>
                        {errors.agreePrivacy && <span className="error-msg">{errors.agreePrivacy}</span>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button type="button" onClick={prevStep} className="btn-prev">
                      <i className="fas fa-arrow-left"></i> Back
                    </button>
                  )}
                  {currentStep < 4 ? (
                    <button type="button" onClick={nextStep} className="btn-next">
                      Continue <i className="fas fa-arrow-right"></i>
                    </button>
                  ) : (
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application <i className="fas fa-paper-plane"></i>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">Have Questions?</span>
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className="faq-grid">
              <div className="faq-item">
                <h3><i className="fas fa-question-circle"></i> How much can I earn?</h3>
                <p>Earnings vary based on property type, location, and seasonality. Hosts typically earn between ₹15,000 to ₹1,00,000+ per month.</p>
              </div>
              <div className="faq-item">
                <h3><i className="fas fa-shield-alt"></i> Is my property protected?</h3>
                <p>Yes, we provide ₹1 crore host protection insurance covering property damage and liability for every booking.</p>
              </div>
              <div className="faq-item">
                <h3><i className="fas fa-clock"></i> How long does verification take?</h3>
                <p>Our team reviews applications within 24-48 hours. Once approved, your property will be listed instantly.</p>
              </div>
              <div className="faq-item">
                <h3><i className="fas fa-wallet"></i> When do I get paid?</h3>
                <p>Payments are automatically processed within 24 hours after guest check-out, directly to your bank account.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style jsx>{`
        .become-host-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        /* Hero Section */
        .host-hero {
          position: relative;
          background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #2e1065 100%);
          padding: 100px 24px 80px;
          overflow: hidden;
        }

        .host-hero-bg {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          opacity: 0.5;
        }

        .host-hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          padding: 8px 20px;
          border-radius: 100px;
          margin-bottom: 24px;
          border: 1px solid rgba(255,255,255,0.2);
        }

        .hero-badge i {
          color: #fbbf24;
        }

        .hero-badge span {
          color: white;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .host-hero-content h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 20px;
        }

        .gradient-text {
          background: linear-gradient(135deg, #fbbf24, #ec4899, #8b5cf6);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .host-hero-content p {
          font-size: 1.2rem;
          color: rgba(255,255,255,0.8);
          margin-bottom: 40px;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 48px;
          flex-wrap: wrap;
        }

        .stat {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 800;
          color: #fbbf24;
        }

        .stat-label {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.7);
        }

        /* Benefits Section */
        .host-benefits {
          padding: 80px 24px;
          background: white;
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-tag {
          display: inline-block;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #8b5cf6;
          background: #f3e8ff;
          padding: 6px 14px;
          border-radius: 100px;
          margin-bottom: 16px;
        }

        .section-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }

        .section-header p {
          color: #64748b;
          font-size: 1.1rem;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 32px;
        }

        .benefit-card {
          text-align: center;
          padding: 32px 24px;
          background: #f8fafc;
          border-radius: 24px;
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }

        .benefit-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 35px -10px rgba(0,0,0,0.1);
          border-color: #8b5cf6;
        }

        .benefit-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .benefit-icon i {
          font-size: 2rem;
          color: white;
        }

        .benefit-card h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
        }

        .benefit-card p {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.6;
        }

        /* Progress Steps */
        .progress-steps {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 24px 0;
        }

        .steps-wrapper {
          display: flex;
          justify-content: space-between;
          max-width: 700px;
          margin: 0 auto;
          position: relative;
        }

        .step {
          flex: 1;
          text-align: center;
          position: relative;
        }

        .step-circle {
          width: 50px;
          height: 50px;
          background: #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          font-size: 1.2rem;
          color: #64748b;
          transition: all 0.3s ease;
          position: relative;
          z-index: 2;
        }

        .step.active .step-circle {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: white;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.2);
        }

        .step.completed .step-circle {
          background: #06d6a0;
          color: white;
        }

        .step-label {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }

        .step.active .step-label {
          color: #8b5cf6;
          font-weight: 600;
        }

        .step-line {
          position: absolute;
          top: 25px;
          left: 50%;
          width: 100%;
          height: 2px;
          background: #e2e8f0;
          z-index: 1;
        }

        .step:last-child .step-line {
          display: none;
        }

        .step.completed .step-line {
          background: #06d6a0;
        }

        /* Form Section */
        .host-form-section {
          padding: 60px 24px 80px;
        }

        .form-card {
          background: white;
          border-radius: 32px;
          padding: 48px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
        }

        .form-step {
          animation: fadeSlideUp 0.4s ease;
        }

        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .step-header {
          margin-bottom: 32px;
        }

        .step-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .step-header p {
          color: #64748b;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #ef4444;
        }

        .error-msg {
          display: block;
          font-size: 0.75rem;
          color: #ef4444;
          margin-top: 6px;
        }

        /* FIXED: Amenities Grid - Proper Alignment */
        .amenities-group {
          margin-bottom: 24px;
        }

        .amenities-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          background: #f8fafc;
          padding: 20px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
        }

        .amenity-checkbox {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: white;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
        }

        .amenity-checkbox:hover {
          border-color: #8b5cf6;
          background: #faf5ff;
        }

        .amenity-checkbox input {
          width: 18px;
          height: 18px;
          cursor: pointer;
          margin: 0;
          accent-color: #8b5cf6;
        }

        .amenity-name {
          font-size: 0.9rem;
          color: #334155;
          font-weight: 500;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 0.9rem;
          color: #475569;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .experience-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .exp-option {
          display: flex;
          align-items: center;
          padding: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .exp-option:hover {
          border-color: #8b5cf6;
          background: #f8fafc;
        }

        .exp-option.active {
          border-color: #8b5cf6;
          background: #f3e8ff;
        }

        .exp-option input {
          margin-right: 12px;
          width: 18px;
          height: 18px;
          accent-color: #8b5cf6;
        }

        .info-box {
          background: #e0f2fe;
          border-radius: 16px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 24px;
        }

        .info-box i {
          font-size: 1.2rem;
          color: #0284c7;
        }

        .info-box p {
          color: #0c4a6e;
          font-size: 0.9rem;
          margin: 0;
        }

        .terms-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .checkbox-wrapper {
          margin-bottom: 16px;
        }

        .form-navigation {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          padding-top: 24px;
          border-top: 1px solid #e2e8f0;
        }

        .btn-prev,
        .btn-next,
        .btn-submit {
          padding: 12px 32px;
          border-radius: 100px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-prev {
          background: white;
          border: 1px solid #e2e8f0;
          color: #475569;
        }

        .btn-prev:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
          background: #faf5ff;
        }

        .btn-next,
        .btn-submit {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border: none;
          color: white;
          margin-left: auto;
        }

        .btn-next:hover,
        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.3);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* FAQ Section */
        .faq-section {
          padding: 60px 24px 80px;
          background: white;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 24px;
        }

        .faq-item {
          padding: 24px;
          background: #f8fafc;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
        }

        .faq-item h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .faq-item h3 i {
          color: #8b5cf6;
        }

        .faq-item p {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0;
        }

        /* Success Page */
        .host-success-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
          padding: 24px;
        }

        .success-card {
          background: white;
          border-radius: 32px;
          padding: 48px;
          text-align: center;
          max-width: 500px;
          width: 100%;
          animation: fadeSlideUp 0.5s ease;
        }

        .success-icon {
          width: 80px;
          height: 80px;
          background: #06d6a0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .success-icon i {
          font-size: 3rem;
          color: white;
        }

        .success-card h1 {
          font-size: 1.8rem;
          color: #1e293b;
          margin-bottom: 16px;
        }

        .success-card p {
          color: #475569;
          margin-bottom: 8px;
        }

        .success-subtitle {
          color: #64748b;
          font-size: 0.9rem;
          margin-bottom: 32px;
        }

        .success-btn {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: white;
          border: none;
          padding: 12px 32px;
          border-radius: 100px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .success-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.3);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .amenities-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .host-hero-content h1 {
            font-size: 2.2rem;
          }
          
          .section-header h2 {
            font-size: 1.8rem;
          }
          
          .form-card {
            padding: 24px;
          }
          
          .steps-wrapper {
            overflow-x: auto;
            padding-bottom: 10px;
          }
          
          .step {
            min-width: 80px;
          }
          
          .hero-stats {
            gap: 24px;
          }
          
          .faq-grid {
            grid-template-columns: 1fr;
          }
          
          .amenities-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            padding: 16px;
          }
          
          .amenity-checkbox {
            padding: 8px 12px;
          }
          
          .amenity-name {
            font-size: 0.85rem;
          }
          
          .btn-prev,
          .btn-next,
          .btn-submit {
            padding: 10px 24px;
          }
        }

        @media (max-width: 480px) {
          .amenities-grid {
            grid-template-columns: 1fr;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default BecomeHost;